import { UseFormReturn } from "react-hook-form";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { PatchPolicyFormValues } from "@/components/patching/schema";
import { Bell, Globe, Clock, Palette } from "lucide-react";

interface GeneralSettingsProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function GeneralSettings({ form }: GeneralSettingsProps) {
  return (
    <div className="space-y-6">
      {/* Application Preferences */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Application Preferences
          </GlassCardTitle>
          <GlassCardDescription>
            Configure basic application settings and preferences
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select defaultValue="UTC">
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select defaultValue="en">
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Notification Settings */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Settings
          </GlassCardTitle>
          <GlassCardDescription>
            Manage how and when you receive notifications
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <FormField
            control={form.control}
            name="show_notifications"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel>Email Notifications</FormLabel>
                  <FormDescription>
                    Receive email notifications for important events
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Browser Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Show browser notifications for real-time updates
              </p>
            </div>
            <Switch defaultChecked />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>System Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Get alerts for system issues and maintenance
              </p>
            </div>
            <Switch defaultChecked />
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Theme and Appearance */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme and Appearance
          </GlassCardTitle>
          <GlassCardDescription>
            Customize the look and feel of the application
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="theme">Theme</Label>
            <Select defaultValue="system">
              <SelectTrigger>
                <SelectValue placeholder="Select theme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Compact Mode</Label>
              <p className="text-sm text-muted-foreground">
                Use a more compact layout to fit more content
              </p>
            </div>
            <Switch />
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Session Settings */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Session Settings
          </GlassCardTitle>
          <GlassCardDescription>
            Configure session timeout and security settings
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
            <Input 
              id="session-timeout"
              type="number" 
              defaultValue="30"
              min="5"
              max="480"
            />
            <p className="text-sm text-muted-foreground">
              Automatically log out after the specified time of inactivity
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Remember Me</Label>
              <p className="text-sm text-muted-foreground">
                Stay logged in across browser sessions
              </p>
            </div>
            <Switch defaultChecked />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Two-Factor Authentication</Label>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security to your account
              </p>
            </div>
            <Button variant="outline" size="sm">
              Configure
            </Button>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}